import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { ComponentPropsWithoutRef, FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ElementsButton } from '../Button/ElementsButton';
import { Icon } from '../Icon';

type CloseOrigin = 'undo' | 'timeout' | 'close';

export interface NotificationBannerProps extends Pick<ComponentPropsWithoutRef<'div'>, 'className'> {
  show: boolean;
  type?: 'error';
  innerKey: string;
  withUndo?: boolean;
  description: string;
  onUndo?: () => void;
  persistent?: boolean;
  durationInMs?: number;
  close: (from?: CloseOrigin) => void;
  customAction?: (close: () => void) => JSX.Element;
  onCloseComplete?: (closeOrigin?: CloseOrigin) => void;
}

const NotificationBanner: FC<NotificationBannerProps> = ({
  durationInMs = 5000,
  persistent = false,
  description,
  onUndo,
  type,
  show,
  className,
  close,
  innerKey,
  customAction,
  withUndo,
}) => {
  const { t: tCommon } = useTranslation('common');

  useEffect(() => {
    if (!persistent && show) {
      const timer = setTimeout(() => {
        close('timeout');
      }, durationInMs);

      return () => clearTimeout(timer);
    }
  }, [show, persistent, durationInMs, close]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          layout
          key={innerKey}
          initial={{ opacity: 0, y: -10 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ ease: 'easeInOut', duration: 0.25 }}
          aria-live="assertive"
          className={clsx('w-max', className)}
        >
          <div className="flex w-full flex-col items-center space-y-4">
            <div className="dark:shadow-classic shadow-lightModeClassic w-full sm:min-w-[470px] overflow-hidden rounded-xl border-b border-[#0D0E0E] bg-darkMode-gray-dark ring-1 ring-background-900 ring-opacity-5">
              <div
                className={clsx(
                  'py-[15px] px-6',
                  type === 'error' &&
                    'bg-darkMode-danger/20 [&_button]:!text-darkMode-darkMode-danger [&_.notification-text]:!text-darkMode-danger [&_svg]:!text-darkMode-danger'
                )}
              >
                <div className="flex items-center">
                  <div className="flex flex-1 items-center justify-between">
                    <p className="notification-text flex-1 text-base font-medium text-darkMode-gray-light">
                      {description}
                    </p>
                    {(onUndo || withUndo) && (
                      <ElementsButton
                        label={tCommon('undo')}
                        onClick={() => {
                          onUndo?.();
                          close('undo');
                        }}
                        className="ml-4 flex-shrink-0 !text-base font-bold text-palette-600 hover:text-palette-600/80"
                      ></ElementsButton>
                    )}
                  </div>
                  {customAction && customAction(() => close('close'))}
                  <button
                    onClick={() => close('close')}
                    className="

                    ml-5
                    h-[40px] w-[40px]
                    flex items-center justify-center
                    overflow-hidden
                    rounded-full border-2
                    border-black bg-palette-600
                    text-black"
                  >
                    <Icon icon="close" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NotificationBanner;
